import type { NestClient } from "../nest/client"
import type { SessionEvent } from "../nest/generated"
import { buildSessionThreadItemsFromSessionEvents, type SessionThreadReference } from "../store/SessionThreadModel"
import { groupStreamEvents } from "../components/events/messageGroups"

interface VoiceMessage {
    role: "user" | "assistant"
    text: string
}

// Use the same provider parsing and message boundaries as the visible task.
export function voiceMessagesFromEvents(reference: SessionThreadReference, events: readonly SessionEvent[]): VoiceMessage[] {
    return buildSessionThreadItemsFromSessionEvents(reference, events).flatMap((item): VoiceMessage[] => {
        if (item.type === "user") return [{ role: "user", text: item.parts.flatMap((part) => (part.type === "text" ? [part.text] : [])).join("\n") }]
        if (item.type !== "provider_events") return []
        const groups = groupStreamEvents(item.events, reference.harnessId)
        const text = groups.findLast((group) => group.type === "text")?.text ?? groups.findLast((group) => group.type === "result")?.result
        return text ? [{ role: "assistant", text }] : []
    })
}

// Registered controller identity permits direct tail reads, without session.read's
// full-history metadata scan. Read through the latest human boundary so chunked
// responses remain complete, even across pages or behind trailing tool events.
export async function readVoiceResponse(client: NestClient, reference: SessionThreadReference): Promise<string | undefined> {
    const events: SessionEvent[] = []
    let cursor: string | undefined
    for (let page = 0; page < 8; page++) {
        const result = await client.requestMethod(
            "session.event.list",
            { sessionId: reference.sessionId, cursor, limit: 128, maxBytes: 64 * 1024 },
            { timeoutMs: 2000 }
        )
        events.push(...result.items)
        const messages = voiceMessagesFromEvents(reference, events)
        const userIndex = messages.findLastIndex((message) => message.role === "user")
        if (userIndex >= 0 || !result.hasMore) return messages.slice(userIndex + 1).findLast((message) => message.role === "assistant")?.text
        if (!result.nextCursor) break
        cursor = result.nextCursor
    }
    // A bounded tail cannot establish the complete response: use the caller's
    // explicit inspect-task result instead of speaking a partial or older answer.
    return undefined
}
